// ═══════════════════════════════════════════════════════════════════════════════
// Helper home — tomorrow tasks action card (title, count, background icon)
// ═══════════════════════════════════════════════════════════════════════════════

import type { KeyboardEvent } from 'react';
import { TomorrowTasksCalendarWatermark } from './TomorrowTasksCalendarWatermark';

type HelperTomorrowTasksActionCardProps = {
  count: number;
  title: string;
  hint: string;
  ariaLabel: string;
  onClick: () => void;
};

export const HelperTomorrowTasksActionCard = ({
  count,
  title,
  hint,
  ariaLabel,
  onClick,
}: HelperTomorrowTasksActionCardProps) => {
  const countDisplay = count > 99 ? '99+' : count;

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <section
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="relative flex min-h-[100px] min-w-0 cursor-pointer flex-col overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-white via-amber-50/80 to-orange-100/70 p-3 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-amber-300 dark:bg-gradient-to-br dark:from-gray-900 dark:via-amber-950/35 dark:to-gray-900"
    >
      <div
        className="pointer-events-none absolute right-4 -top-4 origin-top-right -rotate-[30deg] opacity-[0.55] dark:opacity-80"
        aria-hidden
      >
        <TomorrowTasksCalendarWatermark className="h-[88px] w-[88px] max-w-none" />
      </div>

      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <span className="shrink-0 text-sm font-semibold text-gray-900 min-[400px]:text-sm sm:text-sm dark:text-gray-100">
          {title}
        </span>
        <div className="flex flex-1 items-center justify-center py-2 min-[400px]:py-1 sm:py-2">
          <p className="text-[38px] font-bold leading-none text-gray-900 min-[400px]:text-2xl sm:text-[38px] dark:text-gray-100">
            {countDisplay}
          </p>
        </div>
        <p className="shrink-0 text-sm leading-normal text-gray-600 min-[400px]:max-sm:text-[10px] min-[400px]:max-sm:leading-snug sm:text-sm dark:text-gray-200">
          {hint}
        </p>
      </div>
    </section>
  );
};
